import React, { useState, useEffect } from 'react';
import { Mail, Check } from 'lucide-react';
import { fireEvent } from '../utils/funnelEvents';

// Generate a UUID v4 for event_id dedup. Meta dedupes browser pixel +
// server CAPI events that share the same event_id, so we MUST use the
// same value on both sides of each download click.
export function generateEventId() {
  if (typeof crypto !== 'undefined' && crypto.randomUUID) return crypto.randomUUID();
  // Fallback for browsers without crypto.randomUUID (rare)
  return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, (c) => {
    const r = (Math.random() * 16) | 0;
    return (c === 'x' ? r : (r & 0x3) | 0x8).toString(16);
  });
}

export function readCookie(name) {
  if (typeof document === 'undefined') return null;
  const m = document.cookie.match(new RegExp('(?:^|; )' + name.replace(/[$()*+./?[\\\]^{|}]/g, '\\$&') + '=([^;]*)'));
  return m ? decodeURIComponent(m[1]) : null;
}

// A phone visitor from an ad installs later on a different device, where the
// sortio_attribution cookie doesn't exist, so the mobile email capture is the
// only row that can say which campaign brought them. The waitlist API stores
// no attribution columns, so the campaign rides in company_size (VarChar(100),
// otherwise 'n/a'; the daily digest prints it).
function readMobileAttributionTag() {
  const raw = readCookie('sortio_attribution');
  if (!raw) return 'n/a';
  try {
    const a = JSON.parse(decodeURIComponent(raw));
    const parts = [];
    if (a.utm_source) parts.push('src=' + a.utm_source);
    if (a.utm_medium) parts.push('med=' + a.utm_medium);
    if (a.utm_campaign) parts.push('cmp=' + a.utm_campaign);
    if (a.utm_content) parts.push('cnt=' + a.utm_content);
    if (a.rdt_cid) parts.push('rdt');
    if (a.gclid) parts.push('gclid');
    if (a.fbclid) parts.push('fbclid');
    return parts.length ? parts.join('|').slice(0, 100) : 'n/a';
  } catch {
    return 'n/a';
  }
}

// Is this visitor actually on a phone or tablet?
//
// This used to be decided by viewport width alone, which was wrong: a desktop
// visitor with a narrow window, browser zoom above ~150%, or heavy OS display
// scaling drops under the 768px breakpoint and lost every download link, with
// no way back to them. Roughly half of all "mobile" email captures turned out
// to carry a DESKTOP user agent (landing_event.device, which is UA-derived
// server side) — one of them reloaded /download six times and then went to
// chat to tell us the site thought he was on a phone. So width is now only a
// necessary condition; the device itself has to look handheld too.
function isHandheld() {
  if (typeof navigator === 'undefined') return false;
  const ua = navigator.userAgent || '';
  // Explicit handheld UAs. "Mobile" covers Firefox/Chrome on Android phones,
  // and an Android tablet (no "Mobile" token) still matches on "Android".
  if (/Android|iPhone|iPod|Mobile|Silk|Kindle/i.test(ua)) return true;
  if (/iPad/i.test(ua)) return true;
  // iPadOS 13+ reports itself as "Macintosh". A real Mac has no touchscreen,
  // so multi-touch is what separates an iPad from a MacBook. Note that a
  // touchscreen Windows laptop deliberately does NOT land here: it can run
  // Sortio, so it gets the download cards.
  if (/Macintosh/i.test(ua) && (navigator.maxTouchPoints || 0) > 1) return true;
  return false;
}

// Narrow AND handheld, decided after mount. Starts false so the react-snap
// prerender and the client's first render both show the desktop branch (no
// hydration mismatch, see T0-1).
export function useIsPhone() {
  const [isPhone, setIsPhone] = useState(false);
  useEffect(() => {
    if (typeof window === 'undefined' || !window.matchMedia) return undefined;
    // react-snap prerenders at a 480px viewport, which is below this 767px
    // breakpoint. Without this guard the prerender would bake the phone
    // branch into the static HTML, which (a) drops the platform download
    // links the build-check smoke test requires on /download and (b)
    // mismatches the desktop client's first render, reintroducing the T0-1
    // hydration bug. Flip to the phone branch only in a real browser.
    if (navigator.userAgent === 'ReactSnap') return undefined;
    const mql = window.matchMedia('(max-width: 767px)');
    const update = () => setIsPhone(mql.matches && isHandheld());
    update();
    mql.addEventListener?.('change', update);
    return () => mql.removeEventListener?.('change', update);
  }, []);
  return isPhone;
}

// Mobile visitors cannot install a desktop app. Instead of showing them
// dead-end macOS/Windows installers, capture an email so we can send the
// desktop download link (and seed a return-trigger list). Reuses the
// existing /api/sortio-waitlist/ endpoint (waitlist_type='download_mobile'),
// which emails the link automatically.
//
// `source` names the page the form sits on, so landing_event can tell the
// /download captures from the ones on a landing page.
const MobileDownloadCTA = ({ source = 'download_page', className = '' }) => {
  const [email, setEmail] = useState('');
  const [status, setStatus] = useState('idle'); // idle | submitting | success | error
  const [errorMsg, setErrorMsg] = useState('');

  const submit = async (e) => {
    e.preventDefault();
    const trimmed = email.trim();
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(trimmed)) {
      setStatus('error');
      setErrorMsg('Please enter a valid email address.');
      return;
    }
    setStatus('submitting');
    setErrorMsg('');
    try {
      const res = await fetch('https://app.getsortio.com/api/sortio-waitlist/', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          name: 'Mobile download link request',
          email: trimmed,
          company_size: readMobileAttributionTag(),
          waitlist_type: 'download_mobile',
        }),
      });
      if (!res.ok) throw new Error('Request failed: ' + res.status);

      // Mirror the desktop download-click conversion plumbing so mobile
      // email captures still feed GA4 / Meta lead optimization.
      const eventId = generateEventId();
      fireEvent('mobile_download_email_submit', { method: 'email', source });
      if (window.gtag) {
        window.gtag('event', 'generate_lead', {
          value: 1.0,
          currency: 'USD',
          transaction_id: eventId,
        });
      }
      if (typeof window !== 'undefined' && typeof window.fbq === 'function') {
        window.fbq('track', 'Lead', { content_name: 'sortio_mobile_email' }, { eventID: eventId });
      }
      setStatus('success');
    } catch (err) {
      setStatus('error');
      setErrorMsg('Something went wrong. Please try again.');
    }
  };

  if (status === 'success') {
    return (
      <div className={`apple-panel max-w-md mx-auto p-8 text-center ${className}`}>
        <div className="w-12 h-12 rounded-full bg-sortio-primary/20 flex items-center justify-center mx-auto mb-4">
          <Check className="w-6 h-6 text-sortio-primary" />
        </div>
        <h2 className="text-2xl font-semibold text-apple-50 mb-2">Check your inbox</h2>
        <p className="text-apple-300">
          We'll email you the desktop download link so you can install Sortio when you're at your Mac or PC.
        </p>
      </div>
    );
  }

  return (
    <div className={`apple-panel max-w-md mx-auto p-8 ${className}`}>
      <div className="flex items-center gap-3 mb-4">
        <div className="p-3 bg-apple-800/50 rounded-2xl">
          <Mail className="w-6 h-6 text-sortio-primary" />
        </div>
        <div>
          <h2 className="text-xl font-semibold text-apple-50">Sortio is a desktop app</h2>
          <span className="text-xs text-apple-400">Runs on macOS and Windows</span>
        </div>
      </div>
      <p className="text-apple-300 mb-6">
        You're on a phone, and Sortio installs on your Mac or PC. Enter your email and we'll send you
        the download link for when you're back at your computer.
      </p>
      <form onSubmit={submit} className="space-y-3">
        <input
          type="email"
          inputMode="email"
          autoComplete="email"
          required
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          placeholder="you@example.com"
          aria-label="Email address"
          className="w-full px-4 py-3 rounded-xl bg-apple-900/40 border border-apple-600/40 text-apple-50 placeholder-apple-500 focus:outline-none focus:border-sortio-primary"
        />
        <button
          type="submit"
          disabled={status === 'submitting'}
          className="flex items-center justify-center gap-2 w-full py-3.5 px-6 rounded-xl font-semibold text-base text-white bg-sortio-primary hover:bg-sortio-primary/90 transition-all duration-200 shadow-md hover:shadow-lg disabled:opacity-60"
        >
          <Mail className="w-5 h-5" />
          <span>{status === 'submitting' ? 'Sending...' : 'Email me the download link'}</span>
        </button>
        {status === 'error' && <p className="text-sm text-red-400 text-center">{errorMsg}</p>}
      </form>
    </div>
  );
};

export default MobileDownloadCTA;
